import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  InputAdornment,
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Radio,
} from '@mui/material';
import {
  Download,
  Search,
  Business,
  TrendingUp,
  CheckCircle,
  Schedule,
  Person,
  Phone,
  Email,
  LocationOn,
  Refresh as RefreshIcon,
  People,
  Whatshot,
  AcUnit,
  AssignmentTurnedIn,
  Assignment,
  PhoneCallback,
  BarChart,
  Close as CloseIcon,
  CalendarMonth,
  FilterAlt,
  DateRange,
  Today,
  FileDownload,
} from '@mui/icons-material';
import { getAssociates } from '../../services/associate';
import { leadService } from '../../services/lead';
import { getLeadReport } from '../../services/reports';
import ExportButton from '../../components/ExportButton/ExportButton';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import FormatBadge from '../../components/FormatBadge/FormatBadge';
import { exportAsPDF, exportAsCSV, exportAsExcel } from '../../utils/importExport';
import { showSnackbar } from '../../components/Snackbar';
import AlertModal from '../../components/AlertModal';
import MoreFilters from '../../components/MoreFilters/MoreFilters';


const getStatusConfig = (statusName) => {
  const clean = statusName.toUpperCase();
  if (clean.includes('HOT')) {
    return {
      label: 'HOT',
      color: '#e53935',
      bgColor: '#ffebee',
      icon: <Whatshot sx={{ color: '#e53935' }} />
    };
  }
  if (clean.includes('WARM')) {
    return {
      label: 'WARM',
      color: '#fb8c00',
      bgColor: '#fff3e0',
      icon: <TrendingUp sx={{ color: '#fb8c00' }} />
    };
  }
  if (clean.includes('COLD')) {
    return {
      label: 'COLD',
      color: '#1e88e5',
      bgColor: '#e3f2fd',
      icon: <AcUnit sx={{ color: '#1e88e5' }} />
    };
  }
  if (clean.includes('READY') || clean.includes('VISITED')) {
    return {
      label: clean,
      color: '#3949ab',
      bgColor: '#e8eaf6',
      icon: <Schedule sx={{ color: '#3949ab' }} />
    };
  }
  if (clean.includes('COMPLETE') || clean.includes('DONE')) {
    return {
      label: clean,
      color: '#43a047',
      bgColor: '#e8f5e9',
      icon: <AssignmentTurnedIn sx={{ color: '#43a047' }} />
    };
  }
  if (clean.includes('FOLLOW') || clean.includes('CALL')) {
    return {
      label: clean,
      color: '#ffb300',
      bgColor: '#fff8e1',
      icon: <PhoneCallback sx={{ color: '#ffb300' }} />
    };
  }
  if (clean.includes('NEW')) {
    return {
      label: clean,
      color: '#00897b',
      bgColor: '#e0f2f1',
      icon: <Assignment sx={{ color: '#00897b' }} />
    };
  }
  if (clean.includes('INTERESTED') || clean.includes('WORKING') || clean.includes('PROGRESS')) {
    return {
      label: clean,
      color: '#8e24aa',
      bgColor: '#f3e5f5',
      icon: <CheckCircle sx={{ color: '#8e24aa' }} />
    };
  }
  return {
    label: clean,
    color: '#757575',
    bgColor: '#f5f5f5',
    icon: <BarChart sx={{ color: '#757575' }} />
  };
};

const LeadReport = () => {
  const [page, setPage] = useState(0);
  const [summary, setSummary] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedAssociate, setSelectedAssociate] = useState(null);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    date: '',
    status: '',
    source: '',
    assignedTo: '',
    searchTerm: ''
  });
  const [sortBy, setSortBy] = useState('');
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);

  const sortMapping = {
    leadDateDesc:      { apiSortBy: 'leadDate',      apiSortOrder: 'desc' },
    leadDateAsc:       { apiSortBy: 'leadDate',      apiSortOrder: 'asc'  },
    followUpDateDesc:  { apiSortBy: 'followUpDate',  apiSortOrder: 'desc' },
    followUpDateAsc:   { apiSortBy: 'followUpDate',  apiSortOrder: 'asc'  },
    customerNameAsc:   { apiSortBy: 'customerName',  apiSortOrder: 'asc'  },
    customerNameDesc:  { apiSortBy: 'customerName',  apiSortOrder: 'desc' },
  };

  // Associate dropdown states with pagination
  const [associateOptions, setAssociateOptions] = useState([]);
  const [associateSearchInput, setAssociateSearchInput] = useState('');
  const [loadingAssociates, setLoadingAssociates] = useState(false);
  const associateFetchingRef = useRef(false);
  const associateInitialLoadRef = useRef(false);
  const [associatePage, setAssociatePage] = useState(1);
  const [associateHasMore, setAssociateHasMore] = useState(true);
  const associateSearchTermRef = useRef('');

  // Lead dropdown states with pagination
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadOptions, setLeadOptions] = useState([]);
  const [leadSearchInput, setLeadSearchInput] = useState('');
  const [loadingLeads, setLoadingLeads] = useState(false);
  const leadFetchingRef = useRef(false);
  const leadInitialLoadRef = useRef(false);
  const [leadPage, setLeadPage] = useState(1);
  const [leadHasMore, setLeadHasMore] = useState(true);
  const leadSearchTermRef = useRef('');

  // Lead report data states
  const [leadReportData, setLeadReportData] = useState([]);
  const [loadingLeadReport, setLoadingLeadReport] = useState(false);
  const [totalLeadsCount, setTotalLeadsCount] = useState(0);

  // Export modal states
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState(''); // 'pdf', 'csv', 'excel'
  const [exportDateMode, setExportDateMode] = useState('current'); // 'current', 'custom', 'single', 'all'
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [alertModal, setAlertModal] = useState({ open: false, title: 'Alert', message: '' });
  const closeAlert = () => setAlertModal({ open: false, title: 'Alert', message: '' });


  const sampleData = [
    {
      sno: 1,
      leadId: 'LD001',
      leadDate: '2025-01-10',
      customerName: 'Rahul Verma',
      phone: '+91-9876543210',
      email: 'rahul.verma@email.com',
      source: 'Website',
      status: 'Hot',
      assignedTo: 'Rajesh Kumar',
      followupDate: '2025-01-15',
      location: 'Mumbai',
      priority: 'High'
    },
    {
      sno: 2,
      leadId: 'LD002',
      leadDate: '2025-01-11',
      customerName: 'Priya Patel',
      phone: '+91-9123456780',
      email: 'priya.patel@email.com',
      source: 'Reference',
      status: 'Warm',
      assignedTo: 'Priya Sharma',
      followupDate: '2025-01-16',
      location: 'Delhi',
      priority: 'Medium'
    },
    {
      sno: 3,
      leadId: 'LD003',
      leadDate: '2025-01-12',
      customerName: 'Suresh Kumar',
      phone: '+91-9988776655',
      email: 'suresh.kumar@email.com',
      source: 'Walk-in',
      status: 'Cold',
      assignedTo: 'Vikram Singh',
      followupDate: '2025-01-20',
      location: 'Bangalore',
      priority: 'Low'
    },
    {
      sno: 4,
      leadId: 'LD004',
      leadDate: '2025-01-13',
      customerName: 'Anita Desai',
      phone: '+91-9876543211',
      email: 'anita.desai@email.com',
      source: 'Social Media',
      status: 'Hot',
      assignedTo: 'Amit Sharma',
      followupDate: '2025-01-18',
      location: 'Pune',
      priority: 'High'
    },
    {
      sno: 5,
      leadId: 'LD005',
      leadDate: '2025-01-14',
      customerName: 'Vikram Mehta',
      phone: '+91-9123456781',
      email: 'vikram.mehta@email.com',
      source: 'Advertisement',
      status: 'Warm',
      assignedTo: 'Neha Kumar',
      followupDate: '2025-01-22',
      location: 'Chennai',
      priority: 'Medium'
    }
  ];

  const statusOptions = ['All', 'Hot', 'Warm', 'Cold'];
  const [sourceOptions, setSourceOptions] = useState(['All']);
  const [loadingSources, setLoadingSources] = useState(false);
  const assignedToOptions = ['All', 'Rajesh Kumar', 'Priya Sharma', 'Vikram Singh', 'Amit Sharma', 'Neha Kumar'];

  // Fetch sources from API
  useEffect(() => {
    const fetchSources = async () => {
      setLoadingSources(true);
      try {
        const baseURL = process.env.REACT_APP_API_BASE_URL;
        const response = await fetch(`${baseURL}/api/globalMasterDetails?type=source`);

        if (!response.ok) {
          throw new Error(`Failed to fetch sources: ${response.statusText}`);
        }

        const responseData = await response.json();
        const sources = Array.isArray(responseData.data)
          ? responseData.data
            .filter(item => item.status === true && !item.isDeleted)
            .map(item => item.details?.sourceName || 'Unknown')
            .filter(Boolean)
          : [];

        // Add 'All' option at the beginning
        setSourceOptions(['All', ...sources]);
      } catch (error) {
        console.error('Error fetching sources:', error);
        // Keep default 'All' option on error
        setSourceOptions(['All']);
      } finally {
        setLoadingSources(false);
      }
    };

    fetchSources();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchLeadReportData(newPage + 1, rowsPerPage);
  };

  const handleReset = () => {
    setFilters({
      fromDate: '',
      toDate: '',
      date: '',
      status: 'All',
      source: 'All',
      assignedTo: 'All',
      searchTerm: ''
    });
    setSortBy('');
    setSelectedAssociate(null);
    setSelectedLead(null);
    setAssociateSearchInput('');
    setLeadSearchInput('');
    setPage(0);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchLeadReportData(1, newRowsPerPage);
  };

  // Fetch associates for autocomplete with pagination
  const fetchAssociates = useCallback(async (searchTerm = '', pageNum = 1, append = false) => {
    if (associateFetchingRef.current) return;
    if (!append && !associateHasMore && pageNum > 1) return;

    associateFetchingRef.current = true;
    setLoadingAssociates(true);
    try {
      const params = {
        page: pageNum,
        limit: 20, // Default limit for pagination
      };

      if (searchTerm && searchTerm.trim().length > 0) {
        params.filterKey = searchTerm.trim();
      }

      const response = await getAssociates(params);

      let associates = [];
      let total = 0;

      // Extract data and total from response
      if (response.status && Array.isArray(response.data)) {
        associates = response.data;
        total = parseInt(response.extra?.total || response.totalCount || response.data?.totalDocs || response.count || associates.length, 10);
      } else if (response.success && Array.isArray(response.data)) {
        associates = response.data;
        total = parseInt(response.extra?.total || response.totalCount || response.data?.totalDocs || response.count || associates.length, 10);
      } else if (Array.isArray(response.data?.docs)) {
        associates = response.data.docs;
        total = parseInt(response.extra?.total || response.data.totalDocs || response.totalCount || associates.length, 10);
      }

      const formattedAssociates = associates.map((associate) => ({
        id: associate._id || associate.id,
        name: associate.name || '',
        number: String(associate.associateId || associate._id || ''),
        phone: String(associate.phone || associate.whatsapp || ''),
      }));

      if (append) {
        setAssociateOptions(prev => {
          const newOptions = [...prev, ...formattedAssociates];
          const currentTotal = newOptions.length;
          const hasMore = currentTotal < total && formattedAssociates.length >= params.limit;
          setAssociateHasMore(hasMore);
          return newOptions;
        });
      } else {
        setAssociateOptions(formattedAssociates);
        const currentTotal = formattedAssociates.length;
        const hasMore = currentTotal < total && formattedAssociates.length >= params.limit;
        setAssociateHasMore(hasMore);
      }
    } catch (error) {
      console.error('Error fetching associates:', error);
      if (!append) {
        setAssociateOptions([]);
        setAssociateHasMore(false);
      }
    } finally {
      setLoadingAssociates(false);
      associateFetchingRef.current = false;
    }
  }, []);

  // Debounced associate search - only fetch when user types (length >= 2)
  useEffect(() => {
    const trimmedInput = associateSearchInput.trim();

    // Don't fetch on initial empty load or if input is too short
    if (trimmedInput.length === 0) {
      if (!associateInitialLoadRef.current) {
        associateInitialLoadRef.current = true;
        return;
      }
      return;
    }

    // Only fetch if user has typed at least 2 characters
    if (trimmedInput.length < 2) {
      return;
    }

    // Reset pagination when search term changes
    if (associateSearchTermRef.current !== trimmedInput) {
      setAssociatePage(1);
      setAssociateHasMore(true);
      associateSearchTermRef.current = trimmedInput;
    }

    const timeoutId = setTimeout(() => {
      fetchAssociates(trimmedInput, 1, false);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [associateSearchInput, fetchAssociates]);

  // Fetch leads for autocomplete with pagination
  const fetchLeads = useCallback(async (searchTerm = '', pageNum = 1, append = false) => {
    if (leadFetchingRef.current) return;
    if (!append && !leadHasMore && pageNum > 1) return;

    leadFetchingRef.current = true;
    setLoadingLeads(true);
    try {
      const params = {
        page: pageNum,
        limit: 20, // Default limit for pagination
      };

      if (searchTerm && searchTerm.trim().length > 0) {
        params.filterKey = searchTerm.trim();
      }

      const response = await leadService.getLeads(params);

      let leads = [];
      let total = 0;

      // Extract data from response - response is already json.data from leadService
      if (response && Array.isArray(response.docs)) {
        leads = response.docs;
        total = parseInt(response.totalDocs || response.total || leads.length, 10);
      } else if (Array.isArray(response)) {
        leads = response;
        total = leads.length;
      }

      const formattedLeads = leads.map((lead) => ({
        id: lead._id || lead.id,
        leadId: lead.leadId || '',
        name: lead.customerDetails?.name || lead.name || '',
        phone: String(lead.customerDetails?.phone || lead.phone || ''),
        email: lead.customerDetails?.email || lead.email || '',
        source: lead.source || '',
        status: lead.status || '',
        associateName: lead.associateDetails?.name || '',
        _raw: lead, // Store raw data
      }));

      if (append) {
        setLeadOptions(prev => {
          const newOptions = [...prev, ...formattedLeads];
          const currentTotal = newOptions.length;
          const hasMore = currentTotal < total && formattedLeads.length >= params.limit;
          setLeadHasMore(hasMore);
          return newOptions;
        });
      } else {
        setLeadOptions(formattedLeads);
        const currentTotal = formattedLeads.length;
        const hasMore = currentTotal < total && formattedLeads.length >= params.limit;
        setLeadHasMore(hasMore);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      if (!append) {
        setLeadOptions([]);
        setLeadHasMore(false);
      }
    } finally {
      setLoadingLeads(false);
      leadFetchingRef.current = false;
    }
  }, []);

  // Debounced lead search - only fetch when user types (length >= 2)
  useEffect(() => {
    const trimmedInput = leadSearchInput.trim();

    // Don't fetch on initial empty load or if input is too short
    if (trimmedInput.length === 0) {
      if (!leadInitialLoadRef.current) {
        leadInitialLoadRef.current = true;
        return;
      }
      return;
    }

    // Only fetch if user has typed at least 2 characters
    if (trimmedInput.length < 2) {
      return;
    }

    // Reset pagination when search term changes
    if (leadSearchTermRef.current !== trimmedInput) {
      setLeadPage(1);
      setLeadHasMore(true);
      leadSearchTermRef.current = trimmedInput;
    }

    const timeoutId = setTimeout(() => {
      fetchLeads(trimmedInput, 1, false);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [leadSearchInput, fetchLeads]);

  // Fetch lead report data
  const fetchLeadReportData = useCallback(async (pageNum = 1, limit = 10, overrides = {}) => {
    setLoadingLeadReport(true);
    try {
      const activeSortBy = overrides.sortBy !== undefined ? overrides.sortBy : sortBy;
      const { apiSortBy, apiSortOrder } = (activeSortBy && sortMapping[activeSortBy]) || {};

      const activeDate     = overrides.date     !== undefined ? overrides.date     : filters.date;
      const activeFromDate = overrides.fromDate  !== undefined ? overrides.fromDate : filters.fromDate;
      const activeToDate   = overrides.toDate    !== undefined ? overrides.toDate   : filters.toDate;

      const params = {
        page: pageNum,
        limit: limit,
        fromDate: activeDate ? activeDate : activeFromDate,
        toDate:   activeDate ? activeDate : activeToDate,
        sortBy:    apiSortBy,
        sortOrder: apiSortOrder,
      };

      // Add filter parameters
      const activeStatus = overrides.status !== undefined ? overrides.status : filters.status;
      const activeSource = overrides.source !== undefined ? overrides.source : filters.source;
      if (activeStatus && activeStatus !== 'All') params.status = activeStatus;
      if (activeSource && activeSource !== 'All') params.source = activeSource;
      if (selectedAssociate && selectedAssociate.id) params.associateId = selectedAssociate.id;
      if (selectedLead && selectedLead.leadId) params.leadId = selectedLead.leadId;

      // Remove empty params
      Object.keys(params).forEach(key => { if (!params[key]) delete params[key]; });

      const response = await getLeadReport(params);

      if (response.success && Array.isArray(response.data)) {
        const mappedData = response.data.map((item, index) => ({
          sno: (pageNum - 1) * limit + index + 1,
          leadId: `LD${item.leadId || ''}`,
          leadDate: item.leadDate ? new Date(item.leadDate).toISOString().split('T')[0] : '',
          customerName: item.customerName || '',
          phone: item.contact?.phone || '',
          email: item.contact?.email || '',
          source: item.source || '',
          status: item.status || '',
          assignedTo: item.assignedTo || '',
          followupDate: item.followUpDate ? new Date(item.followUpDate).toISOString().split('T')[0] : '',
          _raw: item,
        }));

        setLeadReportData(mappedData);
        setTotalLeadsCount(response.extra?.total || mappedData.length);
        setSummary(response.summary || null);
      } else {
        setLeadReportData([]);
        setTotalLeadsCount(0);
        setSummary(null);
      }
    } catch (error) {
      console.error('Error fetching lead report:', error);
      setLeadReportData([]);
      setTotalLeadsCount(0);
      setSummary(null);
    } finally {
      setLoadingLeadReport(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.date, filters.fromDate, filters.toDate, filters.status, filters.source, selectedAssociate, selectedLead, sortBy]);

  // Fetch data on initial load and when filters change
  useEffect(() => {
    fetchLeadReportData(1, rowsPerPage);
  }, [fetchLeadReportData, rowsPerPage]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Filter data based on search term (client-side filtering for search term only)
  const getFilteredData = () => {
    if (!filters.searchTerm) {
      return leadReportData;
    }

    const searchTerm = filters.searchTerm.toLowerCase();
    return leadReportData.filter(item =>
      item.customerName.toLowerCase().includes(searchTerm) ||
      item.leadId.toLowerCase().includes(searchTerm) ||
      item.phone.includes(searchTerm) ||
      item.email.toLowerCase().includes(searchTerm)
    );
  };

  const filteredData = getFilteredData();

  // Export functionality
  const buildExportRows = (data) => {
    return data.map((lead, index) => ({
      'S.No': index + 1,
      'Lead ID': lead.leadId,
      'Lead Date': lead.leadDate,
      'Customer Name': lead.customerName,
      'Phone': lead.phone,
      'Email': lead.email,
      'Source': lead.source,
      'Status': lead.status,
      'Assigned To': lead.assignedTo,
      'Followup Date': lead.followupDate,
    }));
  };

  const getExportFileName = () => `Lead_Report_${new Date().toISOString().split('T')[0]}`;

  // Open export dialog instead of directly exporting
  const handleOpenExportDialog = (type) => {
    setExportType(type);
    setExportDateMode('current'); // default to current filters
    // Pre-fill with current filter dates if set
    setExportFromDate(filters.fromDate || '');
    setExportToDate(filters.toDate || '');
    setExportDialogOpen(true);
  };

  const handleCloseExportDialog = () => {
    setExportDialogOpen(false);
    setExportType('');
    setExportLoading(false);
  };

  // Fetch full data with export=true and perform export
  const handleConfirmExport = async () => {
    setExportLoading(true);
    try {
      const params = { export: 'true' };

      // Apply filters only if NOT in "All Dates (Full History)" mode
      if (exportDateMode !== 'all') {
        // Always apply non-date filters (Status, Source, Associate, Lead)
        if (filters.status && filters.status !== 'All') {
          params.status = filters.status;
        }
        if (filters.source && filters.source !== 'All') {
          params.source = filters.source;
        }
        if (selectedAssociate && selectedAssociate.id) {
          params.associateId = selectedAssociate.id;
        }
        if (selectedLead && selectedLead.leadId) {
          params.leadId = selectedLead.leadId;
        }

        // Date range based on selected mode
        if (exportDateMode === 'current') {
          // Use current page filters
          if (filters.fromDate) params.fromDate = filters.fromDate;
          if (filters.toDate) params.toDate = filters.toDate;
        } else if (exportDateMode === 'custom') {
          if (exportFromDate) params.fromDate = exportFromDate;
          if (exportToDate) params.toDate = exportToDate;
        } else if (exportDateMode === 'single') {
          if (exportFromDate) {
            params.fromDate = exportFromDate;
            params.toDate = exportFromDate; // same day
          }
        }
      }

      const response = await getLeadReport(params);

      if (!response.success || !Array.isArray(response.data) || response.data.length === 0) {
        setAlertModal({ open: true, title: 'Warning', message: 'No data found for the selected filter/date range.' });
        setExportLoading(false);
        return;
      }

      // Map API response to export format
      const mappedData = response.data.map((item, index) => ({
        leadId: `LD${item.leadId || ''}`,
        leadDate: item.leadDate ? new Date(item.leadDate).toISOString().split('T')[0] : '',
        customerName: item.customerName || '',
        phone: item.contact?.phone || '',
        email: item.contact?.email || '',
        source: item.source || '',
        status: item.status || '',
        assignedTo: item.assignedTo || '',
        followupDate: item.followUpDate ? new Date(item.followUpDate).toISOString().split('T')[0] : '',
      }));

      const rows = buildExportRows(mappedData);
      const fileName = getExportFileName();

      if (exportType === 'csv') {
        exportAsCSV({ rows, fileNameBase: fileName });
      } else if (exportType === 'pdf') {
        exportAsPDF({ rows, title: 'Lead Report', fileNameBase: fileName, orientation: 'l' });
      } else if (exportType === 'excel') {
        exportAsExcel({ rows, sheetName: 'Leads', fileNameBase: fileName });
      }

      handleCloseExportDialog();
    } catch (error) {
      console.error('Export failed:', error);
      showSnackbar({ type: 'error', message: 'Export failed. Please try again.' });
    } finally {
      setExportLoading(false);
    }
  };

  const derivedSummary = useMemo(() => {
    if (summary) {
      return {
        totalLeads: summary.totalLeads || summary.total || 0,
        pendingAssignment: summary.pendingAssignment || 0,
        distributedSuccessfully: summary.distributedSuccessfully || 0,
        associateLeadReceivers: summary.associateLeadReceivers || 0,
        hotLeads: summary.hotLeads || 0,
        todaysFollowUps: summary.todaysFollowUps || 0,
        overdueFollowUps: summary.overdueFollowUps || 0,
      };
    }

    // Fallback calculation from client-side array
    let hot = 0;
    let pending = 0;
    let distributed = 0;

    leadReportData.forEach(item => {
      const status = (item.status || '').toUpperCase();
      if (status.includes('HOT')) hot++;
      if (status.includes('NEW') || status.includes('PENDING')) pending++;
      else distributed++;
    });

    return {
      totalLeads: leadReportData.length,
      pendingAssignment: pending,
      distributedSuccessfully: distributed,
      associateLeadReceivers: 0,
      hotLeads: hot,
      todaysFollowUps: 0,
      overdueFollowUps: 0,
    };
  }, [summary, leadReportData]);

  // Map summary fields to card items
  const summaryCardItems = useMemo(() => {
    if (!derivedSummary) return [];
    return [
      {
        label: 'TOTAL LEADS',
        value: derivedSummary.totalLeads,
        color: '#536DFE',
        bgColor: 'rgba(83, 109, 254, 0.1)',
        icon: <People sx={{ color: '#536DFE', fontSize: 18 }} />
      },
      {
        label: 'UNASSIGNED LEADS',
        value: derivedSummary.pendingAssignment,
        color: '#fb8c00',
        bgColor: '#fff3e0',
        icon: <Assignment sx={{ color: '#fb8c00', fontSize: 18 }} />
      },
      {
        label: 'ASSIGNED LEADS',
        value: derivedSummary.distributedSuccessfully,
        color: '#43a047',
        bgColor: '#e8f5e9',
        icon: <CheckCircle sx={{ color: '#43a047', fontSize: 18 }} />
      },
      {
        label: 'LEAD RECEIVERS',
        value: derivedSummary.associateLeadReceivers,
        color: '#8e24aa',
        bgColor: '#f3e5f5',
        icon: <Person sx={{ color: '#8e24aa', fontSize: 18 }} />
      },
      {
        label: 'HOT LEADS',
        value: derivedSummary.hotLeads,
        color: '#e53935',
        bgColor: '#ffebee',
        icon: <Whatshot sx={{ color: '#e53935', fontSize: 18 }} />
      },
      {
        label: "TODAY'S FOLLOW-UP",
        value: derivedSummary.todaysFollowUps,
        color: '#00897b',
        bgColor: '#e0f2f1',
        icon: <Schedule sx={{ color: '#00897b', fontSize: 18 }} />
      },
      {
        label: 'OVERDUE FOLLOW-UP',
        value: derivedSummary.overdueFollowUps,
        color: '#ffb300',
        bgColor: '#fff8e1',
        icon: <PhoneCallback sx={{ color: '#ffb300', fontSize: 18 }} />
      }
    ];
  }, [derivedSummary]);

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Lead Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Comprehensive lead management and tracking report
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
            <ExportButton
              onExportPdf={() => handleOpenExportDialog('pdf')}
              onExportCsv={() => handleOpenExportDialog('csv')}
              onExportExcel={() => handleOpenExportDialog('excel')}
            />
            <RefreshButton
              onClick={() => fetchLeadReportData(page + 1, rowsPerPage)}
              loading={loadingLeadReport}
              sx={{
                fontWeight: 600,
                px: 2,
                fontSize: '15px',
                borderRadius: 2,
                height: { xs: 'auto', sm: 40 }
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Summary Cards */}
      {summaryCardItems.length > 0 && (
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 4
        }}>
          {summaryCardItems.map((item) => (
            <Card key={item.label} sx={{
              p: 2,
              boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
              borderRadius: 2.5,
              border: '1px solid #eef2f6',
              bgcolor: '#fff',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flex: '1 1 180px',
              minWidth: '150px',
              maxWidth: '240px'
            }}>
              <Box sx={{ mr: 2 }}>
                <Typography sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '10px', letterSpacing: '0.5px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                  {item.label}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#000', mt: 0.2 }}>
                  {item.value}
                </Typography>
              </Box>
              <Box sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: item.bgColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                {item.icon}
              </Box>
            </Card>
          ))}
        </Box>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Lead Report Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label="Status"
                >
                  {statusOptions.map(status => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Source</InputLabel>
                <Select
                  value={filters.source}
                  onChange={(e) => handleFilterChange('source', e.target.value)}
                  label="Source"
                >
                  {sourceOptions.map(source => (
                    <MenuItem key={source} value={source}>{source}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Autocomplete
                fullWidth
                size="small"
                options={associateOptions}
                getOptionLabel={(option) => option.name ? `${option.name} (${option.number})` : ''}
                value={selectedAssociate}
                onChange={(event, newValue) => {
                  setSelectedAssociate(newValue);
                  setPage(0);
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input' || reason === 'clear') {
                    setAssociateSearchInput(newInputValue);
                    if (reason === 'clear' || (reason === 'input' && newInputValue.trim().length < 2)) {
                      setAssociatePage(1);
                      setAssociateHasMore(true);
                      associateSearchTermRef.current = '';
                    }
                  }
                }}
                onOpen={() => {
                  if (associateOptions.length === 0 && !associateFetchingRef.current) {
                    setAssociatePage(1);
                    setAssociateHasMore(true);
                    fetchAssociates(associateSearchInput || '', 1, false);
                  }
                }}
                loading={loadingAssociates}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    const scrollTop = listboxNode.scrollTop;
                    const scrollHeight = listboxNode.scrollHeight;
                    const clientHeight = listboxNode.clientHeight;
                    const scrollBottom = scrollTop + clientHeight;

                    if (scrollHeight > clientHeight && scrollBottom >= scrollHeight - 50) {
                      if (associateHasMore && !loadingAssociates && !associateFetchingRef.current) {
                        const nextPage = associatePage + 1;
                        setAssociatePage(nextPage);
                        fetchAssociates(associateSearchInput || '', nextPage, true);
                      }
                    }
                  },
                  style: { maxHeight: '300px', overflow: 'auto' },
                }}
                filterOptions={(options, { inputValue }) => {
                  const searchTerm = inputValue.toLowerCase();
                  return options.filter(option =>
                    (option.name || '').toLowerCase().includes(searchTerm) ||
                    String(option.number || '').toLowerCase().includes(searchTerm)
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Associate"
                    placeholder="Type name or number..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <React.Fragment>
                          {loadingAssociates ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.number} {option.phone ? `• ${option.phone}` : ''}
                      </Typography>
                    </Box>
                  </Box>
                )}
                noOptionsText="No associates found"
                clearOnEscape
                selectOnFocus
                handleHomeEndKeys
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Autocomplete
                fullWidth
                size="small"
                options={leadOptions}
                getOptionLabel={(option) => option.name ? `LD${option.leadId} - ${option.name}` : ''}
                value={selectedLead}
                onChange={(event, newValue) => {
                  setSelectedLead(newValue);
                  setPage(0);
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input' || reason === 'clear') {
                    setLeadSearchInput(newInputValue);
                    if (reason === 'clear' || (reason === 'input' && newInputValue.trim().length < 2)) {
                      setLeadPage(1);
                      setLeadHasMore(true);
                      leadSearchTermRef.current = '';
                    }
                  }
                }}
                onOpen={() => {
                  if (leadOptions.length === 0 && !leadFetchingRef.current) {
                    setLeadPage(1);
                    setLeadHasMore(true);
                    fetchLeads(leadSearchInput || '', 1, false);
                  }
                }}
                loading={loadingLeads}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    const scrollTop = listboxNode.scrollTop;
                    const scrollHeight = listboxNode.scrollHeight;
                    const clientHeight = listboxNode.clientHeight;
                    const scrollBottom = scrollTop + clientHeight;

                    if (scrollHeight > clientHeight && scrollBottom >= scrollHeight - 50) {
                      if (leadHasMore && !loadingLeads && !leadFetchingRef.current) {
                        const nextPage = leadPage + 1;
                        setLeadPage(nextPage);
                        fetchLeads(leadSearchInput || '', nextPage, true);
                      }
                    }
                  },
                  style: { maxHeight: '300px', overflow: 'auto' },
                }}
                filterOptions={(options, { inputValue }) => {
                  const searchTerm = inputValue.toLowerCase().trim();
                  return options.filter(option =>
                    (option.name || '').toLowerCase().includes(searchTerm) ||
                    `ld${option.leadId || ''}`.toLowerCase().includes(searchTerm) ||
                    String(option.leadId || '').toLowerCase().includes(searchTerm) ||
                    String(option.phone || '').includes(searchTerm) ||
                    (option.email || '').toLowerCase().includes(searchTerm)
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Lead"
                    placeholder="Type name, ID, phone..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <React.Fragment>
                          {loadingLeads ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        LD{option.leadId} - {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.phone} {option.email ? `• ${option.email}` : ''}
                      </Typography>
                      {option.source && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Source: {option.source} {option.status ? `• Status: ${option.status}` : ''}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                noOptionsText="No leads found"
                clearOnEscape
                selectOnFocus
                handleHomeEndKeys
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    setPage(0);
                    fetchLeadReportData(1, rowsPerPage);
                  }}
                  disabled={loadingLeadReport}
                  startIcon={loadingLeadReport ? <CircularProgress size={16} color="inherit" /> : <Search />}
                  sx={{
                    backgroundColor: '#536DFE',
                    '&:hover': { backgroundColor: '#3F51B5' },
                    textTransform: 'none',
                    fontWeight: 600,
                    height: '40px',
                    borderRadius: 1.5
                  }}
                >
                  {loadingLeadReport ? 'Searching...' : 'Search'}
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleReset}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    height: '40px',
                    borderRadius: 1.5
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <MoreFilters
                expanded={moreFiltersOpen}
                onToggle={() => setMoreFiltersOpen(!moreFiltersOpen)}
                sortBy={sortBy}
                onChangeSortBy={setSortBy}
                type="leadReport"
                height="40px"
                onApply={() => {
                  setPage(0);
                  fetchLeadReportData(1, rowsPerPage);
                }}
                onReset={() => {
                  setSortBy('');
                  setFilters(prev => ({ ...prev, date: '', fromDate: '', toDate: '' }));
                  fetchLeadReportData(1, rowsPerPage, { sortBy: '', date: '', fromDate: '', toDate: '' });
                }}
              >
                {/* Single Date */}
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Single Date"
                  InputLabelProps={{ shrink: true }}
                  value={filters.date}
                  onChange={(e) => setFilters(prev => ({ ...prev, date: e.target.value, fromDate: '', toDate: '' }))}
                />

                {/* Date Range */}
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="From Date"
                      InputLabelProps={{ shrink: true }}
                      value={filters.fromDate}
                      onChange={(e) => setFilters(prev => ({ ...prev, fromDate: e.target.value, date: '' }))}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="To Date"
                      InputLabelProps={{ shrink: true }}
                      value={filters.toDate}
                      onChange={(e) => setFilters(prev => ({ ...prev, toDate: e.target.value, date: '' }))}
                    />
                  </Grid>
                </Grid>
              </MoreFilters>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Lead Report Details
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>S.No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Lead ID</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Lead Date</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Customer Name</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Contact</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Source</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'center' }}>Status</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Assigned To</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Followup Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingLeadReport ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No lead data found for the selected filters
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.map((row, index) => (
                    <TableRow
                      key={row.leadId || row._raw?._id || index}
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#f8f9fa' },
                        '&:hover': { backgroundColor: '#e3f2fd' }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>{row.sno}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.leadId}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.leadDate}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.customerName}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Phone sx={{ fontSize: 14, color: '#666' }} />
                            <Typography variant="body2" sx={{ fontSize: '12px' }}>
                              {row.phone}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Email sx={{ fontSize: 14, color: '#666' }} />
                            <Typography variant="body2" sx={{ fontSize: '12px' }}>
                              {row.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.source}
                          size="small"
                          color="primary"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip
                          label={row.status}
                          size="small"
                          color={row.status === 'Hot' ? 'error' : row.status === 'Warm' ? 'warning' : 'info'}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.assignedTo}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.followupDate}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination

            component="div"
            count={totalLeadsCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              '& .MuiTablePagination-toolbar': {
                backgroundColor: '#f8f9fa',
              }
            }}
          />
        </CardContent>
      </Card>      {/* Export Date Range Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={handleCloseExportDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 16px 48px rgba(83, 109, 254, 0.15), 0 2px 12px rgba(0,0,0,0.06)',
          }
        }}
      >
        {/* Gradient Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #536DFE 0%, #3d5afe 50%, #304ffe 100%)',
          px: 2.5, py: 1.8,
          position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{ position: 'absolute', top: -15, right: -15, width: 55, height: 55, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Box sx={{
                width: 34, height: 34, borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FileDownload sx={{ color: '#fff', fontSize: 18 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem', lineHeight: 1.2 }}>
                  Export Lead Report
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', mt: 0.2 }}>
                  Download your report data
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleCloseExportDialog} size="small" sx={{ color: 'rgba(255,255,255,0.8)', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
          {/* Format Badge */}
          <FormatBadge type={exportType} />

          {/* Section label */}
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#555', mb: 1, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Select Date Range
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            {/* Option 1: Current Filters */}
            <Box
              onClick={() => setExportDateMode('current')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'current' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'current' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'current' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <FilterAlt sx={{ fontSize: 16, color: exportDateMode === 'current' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Current Filters
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {filters.fromDate && filters.toDate
                    ? `${filters.fromDate} → ${filters.toDate}`
                    : filters.fromDate ? `From ${filters.fromDate}` : filters.toDate ? `Till ${filters.toDate}` : 'All dates'}
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'current'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 2: Custom Date Range */}
            <Box
              onClick={() => setExportDateMode('custom')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'custom' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'custom' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'custom' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <DateRange sx={{ fontSize: 16, color: exportDateMode === 'custom' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Custom Date Range
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Choose from & to dates
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'custom'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 3: Single Day */}
            <Box
              onClick={() => setExportDateMode('single')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'single' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'single' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'single' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <Today sx={{ fontSize: 16, color: exportDateMode === 'single' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Single Day
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Export for one specific date
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'single'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 4: All Dates (Full History) */}
            <Box
              onClick={() => setExportDateMode('all')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'all' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'all' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'all' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <CalendarMonth sx={{ fontSize: 16, color: exportDateMode === 'all' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  All Data (Full History)
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Export full database history without any filters
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'all'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>
          </Box>

          {/* Date inputs — compact animated */}
          {exportDateMode === 'custom' && (
            <Box sx={{
              display: 'flex', gap: 1.5, mt: 1.5,
              p: 1.5, borderRadius: 2,
              bgcolor: '#fafbff', border: '1px solid #e8eaf6',
              animation: 'fadeIn 0.2s ease',
              '@keyframes fadeIn': { '0%': { opacity: 0, transform: 'translateY(-4px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } },
            }}>
              <TextField
                label="From Date" type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
              <TextField
                label="To Date" type="date"
                value={exportToDate}
                onChange={(e) => setExportToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
            </Box>
          )}

          {exportDateMode === 'single' && (
            <Box sx={{
              mt: 1.5, p: 1.5, borderRadius: 2,
              bgcolor: '#fafbff', border: '1px solid #e8eaf6',
              animation: 'fadeIn 0.2s ease',
              '@keyframes fadeIn': { '0%': { opacity: 0, transform: 'translateY(-4px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } },
            }}>
              <TextField
                label="Select Date" type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
            </Box>
          )}
        </DialogContent>

        {/* Actions */}
        <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#fafbff', borderTop: '1px solid #eef2f6', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            onClick={handleCloseExportDialog}
            variant="outlined" size="small"
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 600,
              px: 2, fontSize: '0.8rem', borderColor: '#c5cae9', color: '#536DFE',
              '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83,109,254,0.04)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmExport}
            variant="contained" size="small"
            disabled={exportLoading || (exportDateMode === 'custom' && (!exportFromDate || !exportToDate)) || (exportDateMode === 'single' && !exportFromDate)}
            startIcon={exportLoading ? <CircularProgress size={14} color="inherit" /> : <FileDownload sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 700,
              px: 2.5, fontSize: '0.8rem',
              background: 'linear-gradient(135deg, #536DFE 0%, #3d5afe 100%)',
              boxShadow: '0 3px 10px rgba(83, 109, 254, 0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #3d5afe 0%, #304ffe 100%)', boxShadow: '0 4px 14px rgba(83, 109, 254, 0.4)' },
              '&.Mui-disabled': { background: '#e0e0e0', boxShadow: 'none' },
            }}
          >
            {exportLoading ? 'Exporting...' : 'Export Now'}
          </Button>
        </Box>
      </Dialog>
      <AlertModal open={alertModal.open} title={alertModal.title} message={alertModal.message} onClose={closeAlert} />
    </Box>
  );
};

export default LeadReport;

